import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  HiOutlineArrowDownTray,
  HiOutlineArrowRightOnRectangle,
  HiOutlineBanknotes,
  HiOutlineBars3,
  HiOutlineBolt,
  HiOutlineChartBar,
  HiOutlineCheckCircle,
  HiOutlineChevronDown,
  HiOutlineCog6Tooth,
  HiOutlineEllipsisVertical,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineKey,
  HiOutlineLockClosed,
  HiOutlineMoon,
  HiOutlineNoSymbol,
  HiOutlineShieldCheck,
  HiOutlineSquares2X2,
  HiOutlineSun,
  HiOutlineUserCircle,
  HiOutlineUsers,
  HiOutlineViewColumns,
  HiOutlineWallet,
} from 'react-icons/hi2';
import { adminApi, getAdminToken, setAdminToken } from './services/api';
import { rankLabel, rankTitle } from './utils/ranks';
import DataTable from './components/DataTable';
import { DashboardSkeleton } from './components/Skeletons';
import {
  ContractSettings,
  LevelSettings,
  GatewaySettings,
  PlansSettings,
  RankSettings,
  ReferralSettings,
  StakingSettings,
  SupportSettings,
  LaunchSettings,
  TreasurySettings,
} from './pages/SettingsPages';
import './App.css';

const SIDEBAR_KEY = 'dynafi_admin_sidebar_collapsed';
const THEME_KEY = 'dynafi_admin_theme';

const NAV = [
  { to: '/', label: 'Dashboard', end: true, icon: HiOutlineSquares2X2 },
  { to: '/admins', label: 'Admins', icon: HiOutlineShieldCheck },
  { to: '/users', label: 'Users', icon: HiOutlineUsers },
  { to: '/purchases', label: 'Purchases', icon: HiOutlineBanknotes },
  { to: '/stakes', label: 'Stakes', icon: HiOutlineLockClosed },
  { to: '/claims', label: 'Claims', icon: HiOutlineArrowDownTray },
  { to: '/income', label: 'Income', icon: HiOutlineChartBar },
  { to: '/settings', label: 'Settings', icon: HiOutlineCog6Tooth },
];

const SETTINGS_NAV = [
  { to: '/settings/plans', label: 'Plans', description: 'Deposit limits, daily rate, claims, and registration fee' },
  { to: '/settings/staking', label: 'Staking', description: 'Stake limits and lock-period slabs' },
  { to: '/settings/contracts', label: 'Contracts', description: 'Token contracts and chain' },
  { to: '/settings/gateway', label: 'Gateway', description: 'Gateway or owner deposit destination for plan USDT' },
  { to: '/settings/support', label: 'Support', description: 'Telegram and email shown to users' },
  { to: '/settings/launch', label: 'Launch', description: 'Countdown on the public website' },
  { to: '/settings/treasury', label: 'Treasury', description: 'Key used to send DFI claims' },
  { to: '/settings/ranks', label: 'Ranks', description: 'Matching, weekly bonus, and team gates' },
  { to: '/settings/referrals', label: 'Referrals', description: 'Top referrer pool shares' },
  { to: '/settings/levels', label: 'Levels', description: 'Downline commission and unlock rules' },
];

const TITLES = {
  '/': ['Dashboard', 'Platform totals and daily earnings'],
  '/settings': ['Settings', 'Choose a settings group to manage the platform'],
  '/settings/plans': ['Plans', 'Deposit limits, daily rate, claims, and registration fee'],
  '/settings/staking': ['Staking', 'Stake limits and lock-period slabs'],
  '/settings/contracts': ['Contracts', 'Token contracts and chain'],
  '/settings/gateway': ['Gateway', 'Gateway or owner deposit destination for plan USDT'],
  '/settings/support': ['Support', 'Telegram and email shown to users'],
  '/settings/launch': ['Launch', 'Countdown on the public website'],
  '/settings/treasury': ['Treasury', 'Key used to send DFI claims'],
  '/settings/ranks': ['Ranks', 'Matching, weekly bonus, and team gates'],
  '/settings/referrals': ['Referrals', 'Top referrer pool shares'],
  '/settings/levels': ['Levels', 'Downline commission and unlock rules'],
  '/admins': ['Admins', 'Panel accounts stored in the database'],
  '/users': ['Users', 'Registered wallets and balances'],
  '/purchases': ['Purchases', 'USDT plan deposits'],
  '/stakes': ['Stakes', 'DFI staking deposits and claims'],
  '/claims': ['Claims', 'DFI payout requests'],
  '/income': ['Income', 'Credits, partials, and skipped earnings'],
  '/profile': ['Profile', 'Account details and password'],
};

function fmt(value, digits = 4) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString(undefined, { maximumFractionDigits: digits });
}

function statusClass(status) {
  const s = String(status || '').toLowerCase();
  if (['confirmed', 'completed', 'credited', 'active', 'matured', 'claimed'].includes(s)) return 'badge badge--ok';
  if (['pending', 'processing', 'partial', 'claiming'].includes(s)) return 'badge badge--warn';
  if (['failed', 'rejected', 'skipped', 'inactive'].includes(s)) return 'badge badge--bad';
  return 'badge';
}

function when(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

function timeColumn(key, label = 'Created') {
  return {
    key,
    label,
    sort: (row) => row[key] || '',
    search: (row) => (row[key] ? when(row[key]) : ''),
    render: (row) => when(row[key]),
  };
}

function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [theme, setTheme] = useState(() => (document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'));
  const navigate = useNavigate();
  const reduce = useReducedMotion();

  function toggleTheme() {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = next;
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      // ignore storage failures
    }
    setTheme(next);
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const data = await adminApi.login(username, password);
      setAdminToken(data.token);
      navigate('/');
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  const ease = reduce ? { duration: 0 } : { duration: 0.45, ease: [0.22, 1, 0.36, 1] };

  return (
    <div className="login">
      <button
        type="button"
        className="theme-btn login__theme"
        onClick={toggleTheme}
        aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
      >
        {theme === 'dark' ? <HiOutlineSun /> : <HiOutlineMoon />}
      </button>
      <motion.section
        className="login__panel"
        initial={reduce ? false : { opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={ease}
      >
        <aside className="login__hero">
          <div className="brand">
            <img className="brand__mark" src="/logo-256.png" alt="" />
            <div>
              <strong>DynaFi</strong>
              <span>Admin console</span>
            </div>
          </div>
          <h2>Operate the platform from one desk.</h2>
          <ul>
            <li>
              <HiOutlineShieldCheck aria-hidden="true" />
              <span>One active session per account</span>
            </li>
            <li>
              <HiOutlineChartBar aria-hidden="true" />
              <span>Plans, ranks, levels, and payouts</span>
            </li>
            <li>
              <HiOutlineLockClosed aria-hidden="true" />
              <span>Treasury and contract controls</span>
            </li>
          </ul>
        </aside>
        <form className="login__card" onSubmit={onSubmit}>
          <p className="login__kicker">Welcome back</p>
          <h1>Sign in</h1>
          <p>Use your admin username and password.</p>
          <label>
            Username
            <span className="login__field">
              <HiOutlineUserCircle aria-hidden="true" />
              <input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                placeholder="username"
              />
            </span>
          </label>
          <label>
            Password
            <span className="login__field">
              <HiOutlineLockClosed aria-hidden="true" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
              />
              <button
                type="button"
                className="login__reveal"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <HiOutlineEyeSlash /> : <HiOutlineEye />}
              </button>
            </span>
          </label>
          <button type="submit" className="login__submit" disabled={busy}>
            <HiOutlineShieldCheck aria-hidden="true" />
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          {error ? <p className="error">{error}</p> : null}
        </form>
      </motion.section>
    </div>
  );
}

function readCollapsed() {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    return false;
  }
}

function readTheme() {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

function Shell() {
  const navigate = useNavigate();
  const location = useLocation();
  const reduce = useReducedMotion();
  const menuRef = useRef(null);
  const [navOpen, setNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState(readTheme);
  const [profile, setProfile] = useState(null);
  const [title, subtitle] = TITLES[location.pathname] || ['DynaFi', 'Admin'];

  useEffect(() => {
    setNavOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    let active = true;
    adminApi.me().then((data) => {
      if (active) setProfile(data.admin);
    }).catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    function onPointer(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, []);

  function toggleTheme() {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = next;
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      // ignore storage failures
    }
    setTheme(next);
  }

  function logout() {
    setAdminToken('');
    navigate('/login');
  }

  function toggleCollapsed() {
    setCollapsed((value) => {
      const next = !value;
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
      } catch {
        // ignore storage failures
      }
      return next;
    });
  }

  return (
    <div className={`admin${navOpen ? ' is-nav-open' : ''}${collapsed ? ' is-collapsed' : ''}`}>
      <aside className="sidebar">
        <div className="sidebar__head">
          <div className="brand">
            <img className="brand__mark" src="/logo-64.png" alt="" />
            <div className="brand__text">
              <strong>DynaFi</strong>
              <span>Admin</span>
            </div>
          </div>
          <button
            type="button"
            className="rail-toggle"
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!collapsed}
          >
            <HiOutlineViewColumns />
          </button>
        </div>
        <p className="side-kicker">Workspace</p>
        <nav>
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={!!item.end}
                title={item.label}
                className={({ isActive }) => `nav-link${isActive ? ' is-active' : ''}`}
                onClick={() => setNavOpen(false)}
              >
                <Icon className="nav-ico" aria-hidden="true" />
                <span className="nav-label">{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
      </aside>
      {navOpen ? (
        <button type="button" className="backdrop" aria-label="Close menu" onClick={() => setNavOpen(false)} />
      ) : null}
      <div className="admin__main">
        <header className="topbar">
          <div className="topbar__title">
            <button type="button" className="menu-btn" onClick={() => setNavOpen((value) => !value)} aria-label="Open menu">
              <HiOutlineBars3 />
            </button>
            <img className="topbar__logo" src="/logo-64.png" alt="DynaFi" />
            <motion.div
              key={location.pathname}
              initial={reduce ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={reduce ? { duration: 0 } : { duration: 0.24 }}
            >
              <h1>{title}</h1>
              <p>{subtitle}</p>
            </motion.div>
          </div>
          <div className="topbar__tools">
            <button
              type="button"
              className="theme-btn"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <HiOutlineSun /> : <HiOutlineMoon />}
            </button>
            <div className="profile" ref={menuRef}>
              <button type="button" className="profile__btn" onClick={() => setMenuOpen((value) => !value)} aria-expanded={menuOpen}>
                <span className="profile__avatar">{(profile?.display_name || profile?.username || 'A').slice(0, 1).toUpperCase()}</span>
                <span className="profile__meta">
                  <strong>{profile?.display_name || profile?.username || 'Admin'}</strong>
                  <small>{profile?.username || 'Account'}</small>
                </span>
                <HiOutlineChevronDown aria-hidden="true" />
              </button>
              {menuOpen ? (
                <div className="profile__menu">
                  <NavLink to="/profile" onClick={() => setMenuOpen(false)}>
                    <HiOutlineUserCircle aria-hidden="true" />
                    Profile
                  </NavLink>
                  <button type="button" onClick={logout}>
                    <HiOutlineArrowRightOnRectangle aria-hidden="true" />
                    Logout
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            className="admin__body"
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -8 }}
            transition={reduce ? { duration: 0 } : { duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function RequireAuth() {
  if (!getAdminToken()) return <Navigate to="/login" replace />;
  return <Shell />;
}

function SettingsHome() {
  const reduce = useReducedMotion();
  return (
    <section className="settings-home">
      <div className="settings-home__grid">
        {SETTINGS_NAV.map((item, index) => (
          <motion.div
            key={item.to}
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={reduce ? { duration: 0 } : { duration: 0.28, delay: index * 0.03 }}
          >
            <NavLink to={item.to} className="settings-home__card">
              <strong>{item.label}</strong>
              <span>{item.description}</span>
            </NavLink>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Dashboard() {
  const reduce = useReducedMotion();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi
      .dashboard()
      .then((d) => setStats(d.stats))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  async function runReturns() {
    setMsg('');
    setError('');
    try {
      const data = await adminApi.runReturns();
      setMsg(
        `Returns ${data.result.runDate}: credited ${data.result.usersCredited}, skipped ${data.result.usersSkipped || 0}, ROI ${data.result.totalAmount}, upline credits ${data.result.uplinePayments || 0} (${data.result.uplineAmount || 0})`
      );
      const dash = await adminApi.dashboard();
      setStats(dash.stats);
    } catch (e) {
      setError(e.message);
    }
  }

  async function runReferralPool() {
    setMsg('');
    setError('');
    try {
      const data = await adminApi.runReferralPool();
      const result = data.result;
      setMsg(
        result.skipped
          ? `Referral pool ${result.runDate}: skipped (${result.reason || 'already paid'})`
          : `Referral pool ${result.runDate}: ${result.payments || 0} paid, ${result.total || 0} USDT from ${result.deposits || 0} USDT deposits`
      );
      const dash = await adminApi.dashboard();
      setStats(dash.stats);
    } catch (e) {
      setError(e.message);
    }
  }

  if (loading) return <DashboardSkeleton />;

  const cards = [
    ['Users', fmt(stats?.users, 0)],
    ['Admins', fmt(stats?.admins, 0)],
    ['Invested USDT', fmt(stats?.total_invested, 2)],
    ['Staked DFI (locked)', fmt(stats?.staked_active, 2)],
    ['Outstanding balance', fmt(stats?.virtual_liability, 4)],
    ['Claims done', fmt(stats?.claims_completed, 0)],
  ];

  return (
    <section>
      <div className="cards">
        {cards.map(([label, value], index) => (
          <motion.div
            className="card"
            key={label}
            initial={reduce ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={reduce ? { duration: 0 } : { duration: 0.32, delay: index * 0.05 }}
          >
            <span>{label}</span>
            <strong>{value}</strong>
          </motion.div>
        ))}
      </div>
      <div className="panel action-row">
        <div>
          <strong>
            <HiOutlineBolt aria-hidden="true" />
            Daily earnings
          </strong>
          <p className="muted">Credit today’s returns for every eligible user.</p>
        </div>
        <button type="button" onClick={runReturns}>Run daily earnings</button>
      </div>
      <div className="panel action-row">
        <div>
          <strong>
            <HiOutlineUsers aria-hidden="true" />
            Referral pool
          </strong>
          <p className="muted">Pay yesterday’s top-5 referrer pool after that day’s deposits.</p>
        </div>
        <button type="button" onClick={runReferralPool}>Run referral pool</button>
      </div>
      {msg ? <p className="ok">{msg}</p> : null}
      {error ? <p className="error">{error}</p> : null}
    </section>
  );
}

function AdminsPage() {
  const [admins, setAdmins] = useState([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  async function load() {
    const data = await adminApi.admins();
    setAdmins(data.admins || []);
  }

  useEffect(() => {
    load()
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  async function onCreate(e) {
    e.preventDefault();
    setMsg('');
    setError('');
    setBusy(true);
    try {
      await adminApi.createAdmin({
        username,
        password,
        display_name: displayName || undefined,
      });
      setUsername('');
      setPassword('');
      setDisplayName('');
      setMsg('Admin created');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(admin) {
    setMsg('');
    setError('');
    try {
      await adminApi.updateAdmin(admin.id, { is_active: !admin.is_active });
      setMsg(admin.is_active ? 'Admin deactivated' : 'Admin activated');
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function resetPassword(admin) {
    const next = window.prompt(`New password for ${admin.username} (min 8 chars)`);
    if (!next) return;
    setMsg('');
    setError('');
    try {
      await adminApi.updateAdmin(admin.id, { password: next });
      setMsg('Password updated');
    } catch (err) {
      setError(err.message);
    }
  }

  async function removeAdmin(admin) {
    if (!window.confirm(`Delete admin “${admin.username}”?`)) return;
    setMsg('');
    setError('');
    try {
      await adminApi.deleteAdmin(admin.id);
      setMsg('Admin deleted');
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <section>
      <form className="settings panel" onSubmit={onCreate}>
        <h2>Add admin</h2>
        <label>
          Username
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            pattern="[a-zA-Z0-9._-]{3,64}"
            placeholder="jane_admin"
          />
        </label>
        <label>
          Display name
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Optional"
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
          />
        </label>
        <button type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create admin'}
        </button>
      </form>

      <DataTable
        loading={loading}
        rows={admins}
        empty="No admins yet"
        searchPlaceholder="Search admins"
        columns={[
          { key: 'id', label: 'ID', sort: (row) => row.id },
          { key: 'username', label: 'Username', sort: (row) => row.username },
          {
            key: 'display_name',
            label: 'Name',
            sort: (row) => row.display_name || '',
            render: (row) => row.display_name || '—',
          },
          {
            key: 'status',
            label: 'Status',
            sort: (row) => (row.is_active ? 'Active' : 'Inactive'),
            search: (row) => (row.is_active ? 'Active' : 'Inactive'),
            render: (row) => (
              <span className={row.is_active ? 'badge badge--ok' : 'badge badge--bad'}>
                {row.is_active ? 'Active' : 'Inactive'}
              </span>
            ),
          },
          timeColumn('created_at', 'Created'),
          timeColumn('updated_at', 'Updated'),
          {
            key: 'actions',
            label: 'Actions',
            className: 'actions',
            render: (row) => (
              <>
                <button type="button" className="ghost" onClick={() => resetPassword(row)}>
                  Password
                </button>
                <button type="button" className="ghost" onClick={() => toggleActive(row)}>
                  {row.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button type="button" className="ghost danger" onClick={() => removeAdmin(row)}>
                  Delete
                </button>
              </>
            ),
          },
        ]}
      />
      {msg ? <p className="ok">{msg}</p> : null}
      {error ? <p className="error">{error}</p> : null}
    </section>
  );
}

function userIsActive(user) {
  return user?.is_active === 1 || user?.is_active === true;
}

function UserRowMenu({ user, onPick }) {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const active = userIsActive(user);

  useEffect(() => {
    if (!open) return undefined;
    function place() {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = 236;
      const menuHeight = 148;
      const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
      const below = rect.bottom + 6;
      const top = below + menuHeight > window.innerHeight - 8 ? Math.max(8, rect.top - menuHeight - 6) : below;
      setPos({ top, left, width });
    }
    function onPointer(event) {
      if (buttonRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) return;
      setOpen(false);
    }
    function onKey(event) {
      if (event.key === 'Escape') setOpen(false);
    }
    place();
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  function choose(type) {
    setOpen(false);
    onPick(type);
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`ghost row-action${open ? ' is-open' : ''}`}
        aria-label="User actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <HiOutlineEllipsisVertical aria-hidden="true" />
        Actions
      </button>
      {open && pos
        ? createPortal(
            <div
              ref={menuRef}
              className="row-menu"
              role="menu"
              style={{ top: pos.top, left: pos.left, width: pos.width }}
            >
              <button type="button" role="menuitem" className="row-menu__item row-menu__item--balance" onClick={() => choose('balance')}>
                <HiOutlineBanknotes aria-hidden="true" />
                Credit / debit balance
              </button>
              <button type="button" role="menuitem" className={`row-menu__item ${active ? 'row-menu__item--off' : 'row-menu__item--on'}`} onClick={() => choose('status')}>
                {active ? <HiOutlineNoSymbol aria-hidden="true" /> : <HiOutlineCheckCircle aria-hidden="true" />}
                {active ? 'Deactivate user' : 'Activate user'}
              </button>
              <button type="button" role="menuitem" className="row-menu__item row-menu__item--address" onClick={() => choose('address')}>
                <HiOutlineWallet aria-hidden="true" />
                Change address
              </button>
            </div>,
            document.body
          )
        : null}
    </>
  );
}

function UserActionDialog({ action, onClose, onSaved }) {
  const [mode, setMode] = useState(action.type === 'balance' ? 'credit' : '');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [wallet, setWallet] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const user = action.user;

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      let data;
      if (action.type === 'balance') {
        data = await adminApi.adjustUserBalance(user.id, { direction: mode, amount, note });
      } else if (action.type === 'status') {
        data = await adminApi.setUserActive(user.id, !userIsActive(user));
      } else {
        data = await adminApi.changeUserWallet(user.id, wallet);
      }
      onSaved(data.user);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const title = action.type === 'balance' ? 'Credit or debit balance' : action.type === 'status' ? 'Change status' : 'Change address';
  const TitleIcon = action.type === 'balance' ? HiOutlineBanknotes : action.type === 'status' ? (userIsActive(user) ? HiOutlineNoSymbol : HiOutlineCheckCircle) : HiOutlineWallet;
  const tone = action.type === 'balance' ? 'balance' : action.type === 'status' ? (userIsActive(user) ? 'off' : 'on') : 'address';

  return (
    <div className="user-dialog" role="presentation">
      <button type="button" className="user-dialog__backdrop" aria-label="Close" onClick={onClose} disabled={busy} />
      <form className="panel user-dialog__card" onSubmit={submit}>
        <h2 className={`user-dialog__title user-dialog__title--${tone}`}>
          <TitleIcon aria-hidden="true" />
          {title}
        </h2>
        <p className="user-dialog__wallet">{user.wallet_address}</p>
        {action.type === 'balance' ? (
          <>
            <label>
              Action
              <select value={mode} onChange={(event) => setMode(event.target.value)}>
                <option value="credit">Credit</option>
                <option value="debit">Debit</option>
              </select>
            </label>
            <label>
              Amount
              <input inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="0.00" required />
            </label>
            <label>
              Note
              <input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Optional" maxLength={255} />
            </label>
          </>
        ) : null}
        {action.type === 'status' ? (
          <p className="hint">
            This user is {userIsActive(user) ? 'active' : 'inactive'}. {userIsActive(user) ? 'Deactivating' : 'Activating'} stops or restores their login.
          </p>
        ) : null}
        {action.type === 'address' ? (
          <label>
            New wallet address
            <input value={wallet} onChange={(event) => setWallet(event.target.value)} placeholder="0x…" required autoComplete="off" />
          </label>
        ) : null}
        {error ? <p className="error">{error}</p> : null}
        <div className="actions">
          <button type="button" className="ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" disabled={busy}>
            {busy ? 'Saving…' : action.type === 'status' ? (userIsActive(user) ? 'Deactivate' : 'Activate') : 'Save'}
          </button>
        </div>
      </form>
    </div>
  );
}

function UsersPage() {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  useEffect(() => {
    adminApi
      .users()
      .then((d) => setUsers(d.users || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  function openAction(type, user) {
    setError('');
    setNotice('');
    setAction({ type, user });
  }

  return (
    <section>
      {error ? <p className="error">{error}</p> : null}
      {notice ? <p className="ok">{notice}</p> : null}
      <DataTable
        loading={loading}
        rows={users}
        empty="No users yet"
        searchPlaceholder="Search wallets"
        columns={[
          { key: 'id', label: 'ID' },
          { key: 'wallet_address', label: 'Wallet', className: 'mono' },
          {
            key: 'upline_wallet',
            label: 'Upline',
            className: 'mono',
            render: (row) => row.upline_wallet || '—',
          },
          {
            key: 'register_tx_hash',
            label: 'Register tx',
            className: 'mono',
            search: (row) => row.register_tx_hash || '',
            render: (row) =>
              row.register_tx_hash ? (
                <a href={`https://bscscan.com/tx/${row.register_tx_hash}`} target="_blank" rel="noreferrer">
                  {row.register_tx_hash.slice(0, 10)}…
                </a>
              ) : (
                '—'
              ),
          },
          {
            key: 'total_invested',
            label: 'Invested',
            search: (row) => row.total_invested,
            render: (row) => fmt(row.total_invested, 2),
          },
          {
            key: 'total_earned',
            label: 'Earned',
            search: (row) => row.total_earned,
            render: (row) => fmt(row.total_earned, 4),
          },
          {
            key: 'virtual_balance',
            label: 'Balance',
            search: (row) => row.virtual_balance,
            render: (row) => fmt(row.virtual_balance, 4),
          },
          {
            key: 'rank_level',
            label: 'Rank',
            search: (row) => `${row.rank_level ?? 0} ${rankTitle(row.rank_level ?? 0)}`,
            render: (row) => rankLabel(row.rank_level ?? 0),
          },
          {
            key: 'rank_business',
            label: 'Rank business',
            search: (row) => row.rank_business,
            render: (row) => fmt(row.rank_business, 2),
          },
          {
            key: 'status',
            label: 'Status',
            search: (row) => (userIsActive(row) ? 'Active' : 'Inactive'),
            render: (row) => (
              <span className={userIsActive(row) ? 'badge badge--ok' : 'badge badge--bad'}>
                {userIsActive(row) ? <HiOutlineCheckCircle aria-hidden="true" /> : <HiOutlineNoSymbol aria-hidden="true" />}
                {userIsActive(row) ? 'Active' : 'Inactive'}
              </span>
            ),
          },
          timeColumn('created_at', 'Created'),
          timeColumn('register_paid_at', 'Registered'),
          {
            key: 'actions',
            label: 'Actions',
            className: 'actions',
            render: (row) => <UserRowMenu user={row} onPick={(type) => openAction(type, row)} />,
          },
        ]}
      />
      {action ? (
        <UserActionDialog
          action={action}
          onClose={() => setAction(null)}
          onSaved={(user) => {
            setUsers((rows) => rows.map((row) => (row.id === user.id ? user : row)));
            setAction(null);
            setNotice(
              action.type === 'balance'
                ? 'Balance updated'
                : action.type === 'status'
                  ? 'Status updated'
                  : 'Address updated'
            );
          }}
        />
      ) : null}
    </section>
  );
}

function PurchasesPage() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    adminApi
      .purchases()
      .then((d) => setRows(d.purchases || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  return (
    <section>
      {error ? <p className="error">{error}</p> : null}
      <DataTable
        loading={loading}
        rows={rows}
        empty="No purchases yet"
        searchPlaceholder="Search purchases"
        columns={[
          { key: 'id', label: 'ID', sort: (row) => row.id },
          { key: 'wallet_address', label: 'Wallet', className: 'mono', sort: (row) => row.wallet_address },
          {
            key: 'amount_usdt',
            label: 'Amount',
            sort: (row) => row.amount_usdt,
            search: (row) => row.amount_usdt,
            render: (row) => fmt(row.amount_usdt, 2),
          },
          {
            key: 'status',
            label: 'Status',
            sort: (row) => row.status,
            render: (row) => <span className={statusClass(row.status)}>{row.status}</span>,
          },
          { key: 'tx_hash', label: 'Tx', className: 'mono', sort: (row) => row.tx_hash || '', render: (row) => row.tx_hash || '—' },
          { key: 'gateway_address', label: 'Deposit address', className: 'mono', sort: (row) => row.gateway_address || '', render: (row) => row.gateway_address || '—' },
          timeColumn('created_at', 'Created'),
          timeColumn('confirmed_at', 'Confirmed'),
        ]}
      />
    </section>
  );
}

function StakesPage() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    const d = await adminApi.stakes();
    setRows(d.stakes || []);
  }

  useEffect(() => {
    load()
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  async function syncNow() {
    setMsg('');
    setError('');
    try {
      const data = await adminApi.syncStakes();
      setMsg(data.matured ? `Checked deposits · ${data.matured} stake(s) now claimable` : 'Checked pending deposits on BscScan');
      await load();
    } catch (e) {
      setError(e.message);
    }
  }

  const txLink = (hash) => (hash ? <a href={`https://bscscan.com/tx/${hash}`} target="_blank" rel="noreferrer">{hash.slice(0, 10)}…</a> : '—');

  return (
    <section>
      <div className="panel action-row">
        <div>
          <strong>
            <HiOutlineLockClosed aria-hidden="true" />
            Deposits and maturity
          </strong>
          <p className="muted">
            Pending DFI transfers are checked on BscScan every 20 seconds, and ended stakes become claimable every minute. Users claim from their staking page and are paid from the treasury.
          </p>
        </div>
        <button type="button" onClick={syncNow}>Check now</button>
      </div>
      {msg ? <p className="ok">{msg}</p> : null}
      {error ? <p className="error">{error}</p> : null}
      <DataTable
        loading={loading}
        rows={rows}
        empty="No stakes yet"
        searchPlaceholder="Search stakes"
        columns={[
          { key: 'id', label: 'ID', sort: (row) => row.id },
          { key: 'wallet_address', label: 'Wallet', className: 'mono', sort: (row) => row.wallet_address },
          {
            key: 'amount_dfi',
            label: 'Amount (DFI)',
            sort: (row) => Number(row.amount_dfi),
            search: (row) => row.amount_dfi,
            render: (row) => fmt(row.amount_dfi, 2),
          },
          {
            key: 'slab',
            label: 'Slab',
            sort: (row) => Number(row.days),
            search: (row) => `${row.days} days ${Number(row.return_percent)}%`,
            render: (row) => `${row.days} days · ${Number(row.return_percent)}%`,
          },
          {
            key: 'payout_dfi',
            label: 'Payout (DFI)',
            sort: (row) => Number(row.payout_dfi),
            search: (row) => row.payout_dfi,
            render: (row) => fmt(row.payout_dfi, 4),
          },
          {
            key: 'status',
            label: 'Status',
            sort: (row) => row.status,
            search: (row) => `${row.status} ${row.note || ''} ${row.claim_error || ''}`,
            render: (row) => (
              <>
                <span className={statusClass(row.status)}>{row.status}</span>
                {row.note || row.claim_error ? <span className="muted" style={{ display: 'block', fontSize: '0.72rem' }}>{row.note || row.claim_error}</span> : null}
              </>
            ),
          },
          { key: 'deposit_address', label: 'Deposit address', className: 'mono', sort: (row) => row.deposit_address || '' },
          { key: 'tx_hash', label: 'Deposit tx', className: 'mono', sort: (row) => row.tx_hash || '', render: (row) => txLink(row.tx_hash) },
          timeColumn('created_at', 'Created'),
          timeColumn('started_at', 'Started'),
          timeColumn('matures_at', 'Matures'),
          timeColumn('claimed_at', 'Claimed'),
          { key: 'claim_tx_hash', label: 'Claim tx', className: 'mono', sort: (row) => row.claim_tx_hash || '', render: (row) => txLink(row.claim_tx_hash) },
        ]}
      />
    </section>
  );
}

function ClaimsPage() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    adminApi
      .claims()
      .then((d) => setRows(d.claims || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);
  return (
    <section>
      {error ? <p className="error">{error}</p> : null}
      <DataTable
        loading={loading}
        rows={rows}
        empty="No claims yet"
        searchPlaceholder="Search claims"
        columns={[
          { key: 'id', label: 'ID', sort: (row) => row.id },
          { key: 'wallet_address', label: 'Wallet', className: 'mono', sort: (row) => row.wallet_address },
          {
            key: 'amount_virtual',
            label: 'Amount',
            sort: (row) => row.amount_virtual,
            search: (row) => row.amount_virtual,
            render: (row) => fmt(row.amount_virtual, 4),
          },
          {
            key: 'charge_amount',
            label: 'Charge',
            sort: (row) => row.charge_amount,
            search: (row) => `${row.charge_amount} ${row.charge_percent}`,
            render: (row) => {
              const charge = Number(row.charge_amount);
              const percent = Number(row.charge_percent);
              if (!Number.isFinite(charge) || charge <= 0) return '—';
              return `${fmt(charge, 4)}${Number.isFinite(percent) ? ` (${fmt(percent, 2)}%)` : ''}`;
            },
          },
          {
            key: 'amount_dfi',
            label: 'DFI sent',
            sort: (row) => row.amount_dfi,
            search: (row) => row.amount_dfi,
            render: (row) => fmt(row.amount_dfi, 4),
          },
          {
            key: 'charge_distributed',
            label: 'Upline paid',
            sort: (row) => row.charge_distributed,
            search: (row) => row.charge_distributed,
            render: (row) => {
              const paid = Number(row.charge_distributed);
              return Number.isFinite(paid) && paid > 0 ? fmt(paid, 4) : '—';
            },
          },
          {
            key: 'status',
            label: 'Status',
            sort: (row) => row.status,
            render: (row) => <span className={statusClass(row.status)}>{row.status}</span>,
          },
          {
            key: 'tx_hash',
            label: 'Tx',
            className: 'mono',
            sort: (row) => row.tx_hash || '',
            render: (row) => row.tx_hash || '—',
          },
          timeColumn('created_at', 'Created'),
          timeColumn('completed_at', 'Completed'),
        ]}
      />
    </section>
  );
}

function IncomePage() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    adminApi
      .income(status || undefined)
      .then((d) => {
        if (active) setRows(d.events || []);
      })
      .catch((e) => {
        if (active) setError(e.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [status]);
  return (
    <section>
      {error ? <p className="error">{error}</p> : null}
      <div className="filter-row">
        <span>Filter</span>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All</option>
          <option value="credited">Credited</option>
          <option value="partial">Partial</option>
          <option value="skipped">Skipped</option>
        </select>
      </div>
      <DataTable
        loading={loading}
        rows={rows}
        filterKey={status}
        empty="No income events"
        searchPlaceholder="Search income"
        columns={[
          { key: 'id', label: 'ID', sort: (row) => row.id },
          { key: 'wallet_address', label: 'Wallet', className: 'mono', sort: (row) => row.wallet_address },
          { key: 'source_type', label: 'Source', sort: (row) => row.source_type },
          {
            key: 'amount_requested',
            label: 'Requested',
            sort: (row) => row.amount_requested,
            search: (row) => row.amount_requested,
            render: (row) => fmt(row.amount_requested, 4),
          },
          {
            key: 'amount_credited',
            label: 'Credited',
            sort: (row) => row.amount_credited,
            search: (row) => row.amount_credited,
            render: (row) => fmt(row.amount_credited, 4),
          },
          {
            key: 'status',
            label: 'Status',
            sort: (row) => row.status,
            render: (row) => <span className={statusClass(row.status)}>{row.status}</span>,
          },
          {
            key: 'reason',
            label: 'Reason',
            sort: (row) => row.skip_reason || row.note || '',
            search: (row) => row.skip_reason || row.note || '',
            render: (row) => row.skip_reason || row.note || '—',
          },
          timeColumn('created_at', 'Created'),
        ]}
      />
    </section>
  );
}

function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.me().then((data) => setProfile(data.admin)).catch((err) => setError(err.message));
  }, []);

  async function onSubmit(event) {
    event.preventDefault();
    setMsg('');
    setError('');
    if (password !== confirm) {
      setError('New passwords do not match');
      return;
    }
    setBusy(true);
    try {
      const data = await adminApi.changePassword({ currentPassword, password });
      setProfile(data.admin);
      setCurrentPassword('');
      setPassword('');
      setConfirm('');
      setMsg('Password updated');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="profile-page">
      <div className="panel profile-card">
        <span className="profile__avatar profile__avatar--lg">{(profile?.display_name || profile?.username || 'A').slice(0, 1).toUpperCase()}</span>
        <div>
          <h2>{profile?.display_name || profile?.username || 'Admin'}</h2>
          <p className="muted">{profile?.username || 'Loading account…'}</p>
        </div>
      </div>
      <form className="settings panel" onSubmit={onSubmit}>
        <h2>
          <HiOutlineKey aria-hidden="true" />
          Change password
        </h2>
        <label>
          Current password
          <input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required autoComplete="current-password" />
        </label>
        <label>
          New password
          <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required minLength={8} autoComplete="new-password" />
        </label>
        <label>
          Confirm new password
          <input type="password" value={confirm} onChange={(event) => setConfirm(event.target.value)} required minLength={8} autoComplete="new-password" />
        </label>
        <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Update password'}</button>
      </form>
      {msg ? <p className="ok">{msg}</p> : null}
      {error ? <p className="error">{error}</p> : null}
    </section>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<RequireAuth />}>
        <Route path="/" element={<Dashboard />} />
        <Route path="/settings" element={<SettingsHome />} />
        <Route path="/settings/plans" element={<PlansSettings />} />
        <Route path="/settings/staking" element={<StakingSettings />} />
        <Route path="/settings/contracts" element={<ContractSettings />} />
        <Route path="/settings/gateway" element={<GatewaySettings />} />
        <Route path="/settings/support" element={<SupportSettings />} />
        <Route path="/settings/launch" element={<LaunchSettings />} />
        <Route path="/settings/treasury" element={<TreasurySettings />} />
        <Route path="/settings/ranks" element={<RankSettings />} />
        <Route path="/settings/referrals" element={<ReferralSettings />} />
        <Route path="/settings/levels" element={<LevelSettings />} />
        <Route path="/admins" element={<AdminsPage />} />
        <Route path="/users" element={<UsersPage />} />
        <Route path="/purchases" element={<PurchasesPage />} />
        <Route path="/stakes" element={<StakesPage />} />
        <Route path="/claims" element={<ClaimsPage />} />
        <Route path="/income" element={<IncomePage />} />
        <Route path="/profile" element={<ProfilePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
